// Growth-mindset sticky note — "Today's nudge". A lightly-rotated post-it with
// a bit of "tape", a tone-matched quote, smooth crossfade rotation, and
// tap-to-shuffle. Compact enough to sit in the dashboard's right rail without
// competing with the daily goal and garden content. Encouraging, not distracting.
import { useCallback, useEffect, useRef, useState } from 'react';
import { pickQuoteIndex, quoteAt, type QuoteProgress } from '@/lib/quoteEngine';
import { fontPx } from '@/lib/fontSize';

const ROTATE_MS = 60_000;
const FADE_MS = 220;

export function StickyQuote({ progress }: { progress: QuoteProgress }) {
  const [idx, setIdx] = useState<number>(() => pickQuoteIndex(progress));
  const [shown, setShown] = useState(true);
  const progressRef = useRef(progress);
  progressRef.current = progress;

  const advance = useCallback(() => {
    setShown(false);
    window.setTimeout(() => {
      setIdx(pickQuoteIndex(progressRef.current));
      setShown(true);
    }, FADE_MS);
  }, []);

  // Gentle periodic rotation. Respects reduced-motion by skipping the interval.
  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    const id = window.setInterval(advance, ROTATE_MS);
    return () => window.clearInterval(id);
  }, [advance]);

  const quote = quoteAt(idx);

  return (
    <div
      role="note"
      aria-label="Daily motivation"
      title="Tap for another"
      onClick={advance}
      style={{
        position: 'relative',
        cursor: 'pointer',
        width: '100%',
        minHeight: 142,
        display: 'flex',
        flexDirection: 'column',
        transform: 'rotate(-1.4deg)',
        background: 'var(--note-bg)',
        color: 'var(--note-text)',
        borderRadius: 8,
        padding: '14px 15px 12px',
        boxShadow: 'var(--shadow-md)',
        fontFamily: 'var(--font-sans)',
        overflow: 'hidden',
      }}
    >
      {/* tape */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: -9,
          left: '50%',
          transform: 'translateX(-50%) rotate(1.5deg)',
          width: 76,
          height: 16,
          background: 'var(--note-tape)',
          border: '1px solid var(--note-tape-edge)',
          borderRadius: 2,
          boxShadow: '0 1px 2px rgba(0,0,0,.06)',
        }}
      />
      <div
        style={{
          fontSize: fontPx(10),
          fontWeight: 800,
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          opacity: 0.6,
        }}
      >
        Today’s nudge
      </div>
      <p
        aria-live="polite"
        style={{
          margin: '5px 0 6px',
          fontSize: fontPx(13.5),
          lineHeight: 1.35,
          fontWeight: 600,
          flex: 1,
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          opacity: shown ? 1 : 0,
          transform: shown ? 'translateY(0)' : 'translateY(3px)',
          transition: `opacity ${FADE_MS}ms ease, transform ${FADE_MS}ms ease`,
        }}
      >
        {quote.text}
      </p>
      <div style={{ fontSize: fontPx(9.5), fontWeight: 700, opacity: 0.5, textAlign: 'right' }}>
        tap for another
      </div>
    </div>
  );
}
